import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Plus,
  X,
  Search,
  Trash2,
  Eye,
  Pencil,
  CirclePlus,
  ChevronLeft,
  CircleAlert,
} from "lucide-react";
import Select from "react-select";
import { useParams, useNavigate } from "react-router-dom";
import { BASE_URL } from "../apiConfig";
import { message } from "antd";
import "./activerole.css";
import "./activeclient.css";

const ActiveRoles = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  // Form state
  const [roleName, setRoleName] = useState("");
  const [pocName, setPocName] = useState("");
  const [pocContact, setPocContact] = useState("");
  const [pocEmail, setPocEmail] = useState("");
  const [architects, setArchitects] = useState([]);
  const [selectedArchitects, setSelectedArchitects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchitectDropdown, setShowArchitectDropdown] = useState(false);
  const [calculationMethod, setCalculationMethod] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [errors, setErrors] = useState({
    roleName: "",
    pocName: "",
    pocContact: "",
    pocEmail: "",
    // calculationMethod: "",
    selectedArchitects: "",
  });

  // Fetch roles for the client when component mounts
  useEffect(() => {
    fetchRoles();
  }, [clientId]);

  // Fetch architects when modal opens
  useEffect(() => {
    if (isModalOpen || isEditModalOpen) {
      fetchArchitects();
    }
  }, [isModalOpen, isEditModalOpen]);

  const fetchRoles = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      // Fetch roles for this client
      const rolesResponse = await fetch(
        `${BASE_URL}/client/role_list_under_client?client_id=${clientId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!rolesResponse.ok) {
        throw new Error("Failed to fetch roles");
      }

      const rolesData = await rolesResponse.json();

      // Make sure rolesData is an array
      if (Array.isArray(rolesData)) {
        setRoles(rolesData);
      } else if (rolesData && typeof rolesData === "object") {
        // If it's an object with data property that's an array
        if (Array.isArray(rolesData.data)) {
          setRoles(rolesData.data);
        } else {
          // If it's another structure, log it for debugging
          console.log("API Response structure:", rolesData);
          setRoles([]);
        }
      } else {
        console.error("Unexpected API response format:", rolesData);
        setRoles([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setLoading(false);
    }
  };

  const fetchArchitects = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await fetch(
        `${BASE_URL}/user/get_users_on_user_type?user_type=Project%20Architect`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch architects");
      }

      const data = await response.json();
      const users = Array.isArray(data) ? data : data.data || [];

      const architectsList = users.map((user) => ({
        name: `${user.first_name} ${user.last_name}`.trim(),
        user_id: user.user_id,
      }));

      setArchitects(architectsList);
    } catch (error) {
      console.error("Error fetching architects:", error);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to the clients page
  };

  const handleAddRole = () => {
    // Reset form state for adding new role
    setRoleName("");
    setPocName("");
    setPocContact("");
    setPocEmail("");
    setSelectedArchitects([]);
    setSearchTerm("");
    setCalculationMethod("");
    // Clear errors
    setErrors({
      roleName: "",
      pocName: "",
      pocContact: "",
      pocEmail: "",
      // calculationMethod: "",
      selectedArchitects: "",
    });

    // Open the modal
    setIsModalOpen(true);
  };

  const handleViewProject = async (roleId) => {
    navigate(`/dashboard/${roleId}/viewprojects`);
  };

  const handleEditRole = async (roleId) => {
    setIsEditModalOpen(true);

    try {
      setFormLoading(true);

      // Fetch role details and populate form
      const roleDetails = roles.find((role) => role.id === roleId);
      console.log(roleDetails);

      if (roleDetails) {
        console.log("Role details fetched:", roleDetails); // Debug log

        // Set current role
        setCurrentRole(roleDetails);

        // Set form values from role details
        setRoleName(roleDetails.role_name || "");
        setPocName(roleDetails.poc_name || "");
        setPocContact(roleDetails.poc_contact_number || "");
        setPocEmail(roleDetails.poc_mail_id || "");

        // Set calculation method based on project_type
        if (roleDetails.project_type === 1) {
          setCalculationMethod("maximum");
        } else if (roleDetails.project_type === 2) {
          setCalculationMethod("combined");
        } else {
          setCalculationMethod("");
        }

        // Set selected architects
        if (
          roleDetails.assigned_users &&
          Array.isArray(roleDetails.assigned_users)
        ) {
          const selectedUsers = roleDetails.assigned_users.map((user) => ({
            name: `${user.first_name} ${user.last_name}`.trim(),
            user_id: user.user_id,
          }));
          setSelectedArchitects(selectedUsers);
        } else {
          setSelectedArchitects([]);
        }

        // Reset search and dropdown state
        setSearchTerm("");
        setShowArchitectDropdown(false);

        // Clear any previous errors
        setErrors({
          roleName: "",
          pocName: "",
          pocContact: "",
          pocEmail: "",
          // calculationMethod: "",
          selectedArchitects: "",
        });

        // Open edit modal - make sure this is being called
        console.log("Edit modal should be open now:", isEditModalOpen); // Debug log
      }
    } catch (error) {
      console.error("Error in handleEditRole:", error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    // Reset form state
    setRoleName("");
    setPocName("");
    setPocContact("");
    setPocEmail("");
    setSelectedArchitects([]);
    setSearchTerm("");
    setCalculationMethod("");
    setShowArchitectDropdown(false);
    setCurrentRole(null);

    // Clear errors
    setErrors({
      roleName: "",
      pocName: "",
      pocContact: "",
      pocEmail: "",
      // calculationMethod: "",
      selectedArchitects: "",
    });

    // Close modals
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setShowDeleteConfirm(false);
    setShowDeleteModal(false);
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      roleName: "",
      pocName: "",
      pocContact: "",
      pocEmail: "",
      calculationMethod: "",
      selectedArchitects: "",
    };

    // Validate Role Name
    if (!roleName.trim()) {
      newErrors.roleName = "This field cannot be empty";
      isValid = false;
    } else if (roleName.trim().length < 3) {
      newErrors.roleName =
        "Minimum Character Limit not met, please contact Admin";
      isValid = false;
    } else if (roleName.trim().length > 50) {
      newErrors.roleName =
        "Maximum Character Limit Exceeded, please recheck or contact Admin";
      isValid = false;
    }

    // Validate POC Name
    if (!pocName.trim()) {
      newErrors.pocName = "This field cannot be empty";
      isValid = false;
    } else if (pocName.trim().length < 3) {
      newErrors.pocName =
        "Minimum Character Limit not met, please contact Admin";
      isValid = false;
    } else if (pocName.trim().length > 50) {
      newErrors.pocName =
        "Maximum Character Limit Exceeded, please recheck or contact Admin";
      isValid = false;
    }

    // Validate POC Contact
    const phoneRegex = /^\+?[0-9]{10}$/;
    if (!pocContact.trim()) {
      newErrors.pocContact = "This field cannot be empty";
      isValid = false;
    } else if (!phoneRegex.test(pocContact.trim())) {
      newErrors.pocContact = "Please enter a valid phone number (10 digits)";
      isValid = false;
    }

    // Validate POC email
    if (!pocEmail.trim()) {
      newErrors.pocEmail = "This field cannot be empty";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(pocEmail)) {
      newErrors.pocEmail = "Please enter a valid email address";
      isValid = false;
    }

    // Validate architects selection
    if (selectedArchitects.length === 0) {
      newErrors.selectedArchitects = "Please select a project architect";
      isValid = false;
    }

    // Validate calculation method
    // if (!calculationMethod) {
    //   newErrors.calculationMethod = "This field cannot be empty";
    //   isValid = false;
    // }

    setErrors(newErrors);
    return isValid;
  };
  const confirmDeleteRole = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }
      const response = await fetch(
        `${BASE_URL}/project/get_project_count_under_jobrole`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            jobrole_id: currentRole.id,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error("Failed to check role deletion");
      }
      if (data === 0) {
        setShowDeleteModal(true);
      } else {
        setShowWarningModal(true);
        // messageApi.error("Role cannot be deleted until all associated Projects are deleted.");
      }
    } catch (error) {
      console.error("Error checking role deletion:", error);
      messageApi.error("Failed to check role deletion");
    }
  };

  const cancelDelete = () => {
    // Close the delete confirmation modal
    setShowDeleteModal(false);
  };

  const confirmDeleteUser = async () => {
    // Execute the delete operation
    await handleDelete();
  };

  const toggleArchitectDropdown = () => {
    setShowArchitectDropdown(!showArchitectDropdown);
  };

  const handleArchitectSelect = (architect) => {
    if (selectedArchitects.some((a) => a.user_id === architect.user_id)) {
      setSelectedArchitects(
        selectedArchitects.filter((a) => a.user_id !== architect.user_id)
      );
    } else {
      setSelectedArchitects([...selectedArchitects, architect]);
    }
    // Clear error when an architect is selected
    if (errors.selectedArchitects && selectedArchitects.length === 0) {
      setErrors({ ...errors, selectedArchitects: "" });
    }
  };

  const removeArchitect = (architectUserId) => {
    setSelectedArchitects(
      selectedArchitects.filter((a) => a.user_id !== architectUserId)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setFormLoading(true);

    const token = localStorage.getItem("accessToken");

    const roleData = {
      client_id: clientId,
      role_name: roleName,
      poc_name: pocName,
      poc_contact_number: pocContact,
      poc_mail_id: pocEmail,
      assigned_users: selectedArchitects.map((a) => a.user_id),
      project_type: calculationMethod === "maximum" ? 1 : 2,
    };

    try {
      const response = await fetch(`${BASE_URL}/client/create_role_in_client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(roleData),
      });

      const data = await response.json();

      if (!response.ok) {
        messageApi.error(data.detail);
      }

      // if (response.status === 409) {
      //     messageApi.error("Duplicate entry, please enter another role name");
      //     return;
      // }

      // Success - refresh roles and close modal
      else {
        fetchRoles();
        handleCloseModal();
        messageApi.success("Role added successfully!");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      // if (error.status === 409) {
      //     messageApi.error("Duplicate entry, please enter another name");
      // } else {
      //     messageApi.error("An error occurred while adding the role");
      // }
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setFormLoading(true);

    const token = localStorage.getItem("accessToken");

    // Payload for updating role
    const roleData = {
      id: currentRole.id,
      client_id: clientId,
      role_name: roleName,
      poc_name: pocName,
      poc_contact_number: pocContact,
      poc_mail_id: pocEmail,
      assigned_users: selectedArchitects.map((a) => a.user_id),
      project_type: calculationMethod === "maximum" ? 1 : 2,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/client/update_role_under_client`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(roleData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // throw new Error(`Failed to update user. Status: ${response.status}. ${responseData.message || ''}`);
        messageApi.error(data.detail);
      } else {
        fetchRoles();
        handleCloseModal();
        // alert("User updated successfully");
        messageApi.success("Role Updated successfully!");
      }
    } catch (error) {
      console.error("Error updating client:", error);
      if (error.status === 409) {
        messageApi.error("Duplicate entry, please enter another name");
        return;
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);

    const token = localStorage.getItem("accessToken");

    try {
      const response = await fetch(
        `${BASE_URL}/client/delete_role_under_client?role_id=${currentRole.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete role");
      }

      // Success - refresh roles and close modal
      fetchRoles();
      handleCloseModal();
      messageApi.success("Role deleted successfully!");
    } catch (error) {
      console.error("Error deleting role:", error);
      messageApi.error("Failed to delete role");
    } finally {
      setFormLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Function to get logo or icon for a role
  const getRoleIcon = (role) => {
    if (role.client_details && role.client_details.image) {
      return (
        <div className="role-image-container">
          <img
            src={role.client_details.image}
            alt={role.role_name || "Role"}
            className="role-image"
          />
        </div>
      );
    }

    // Default icon if no image is available
    return <div className="role-default-icon">🔥</div>;
  };

  // Filter architects based on search term
  const filteredArchitects = architects.filter((architect) =>
    architect.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="role-loading-container">
        <div className="role-loading">Loading roles...</div>
      </div>
    );
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const calculationOptions = [
    { value: "maximum", label: "Maximum KPI" },
    { value: "combined", label: "Combined KPI" },
  ];

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors.calculationMethod ? "red" : "#ccc",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#C12328",
      },
      fontSize: "14px",
      padding: "2px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#C12328" : "white", // red on hover
      color: state.isFocused ? "white" : "black",
      cursor: "pointer",
      fontSize: "14px",
      outline: "none",
    }),
    placeholder: (base) => ({
      ...base,
      // color: "#666",
    }),
    singleValue: (base) => ({
      ...base,
      // color: "#000",
    }),
  };

  const filteredRoles = roles.filter((role) =>
  role.role_name.toLowerCase().includes(searchTerm.toLowerCase())
);

  return (
    <>
      {contextHolder}
      <div className="role-sidebar"></div>

      <div className="role-container">
        <div className="role-container-inner">
          <div className="role-header">
            <button className="role-back-button" onClick={handleBack}>
              <ChevronLeft size={20} />
              <span className="role-header-text">
                {roles.length === 1 ? "Active Role" : "Active Roles"}
              </span>
            </button>
            <button className="role-add-button" onClick={handleAddRole}>
              <span className="button_text">Add New Role</span>
            </button>
          </div>

          <div className="client-search-container">
            <input
              type="text"
              placeholder="Search Role...."
              value={searchTerm}
              onChange={handleSearchChange}
              className="client-search-input"
            />
            <button className="client-search-btn">
              <span className="client-search-icon">
                <Search size={16} />
              </span>
            </button>
          </div>

          <div className="role-grid">
            {!Array.isArray(filteredRoles) || filteredRoles.length === 0 ? (
              <div className="role-no-data">
                <p>
                  No roles found
                  {searchTerm ? " matching your search" : " for this client"}.
                </p>
              </div>
            ) : (
              filteredRoles.map((role) => (
                <div key={role.id} className="role-card">
                  <div className="role-info">
                    <div className="role-name">{role.role_name}</div>
                    <div className="role-icon">{getRoleIcon(role)}</div>
                  </div>
                  <div className="client-actions">
                    <button
                      className="client-edit-btn"
                      onClick={() => handleEditRole(role.id)}
                    >
                      <span className="client-edit-icon">
                        <Pencil size={14} />
                      </span>{" "}
                      Edit Role
                    </button>
                    <button
                      className="client-view-role-btn"
                      onClick={() => handleViewProject(role.id)}
                    >
                      <span className="client-eye-icon">
                        <Eye size={14} />
                      </span>{" "}
                      View Project
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Role Modal */}
      {isModalOpen && (
        <div className="role-modal-overlay">
          <div className="role-modal">
            <div className="role-modal-header">
              <div className="role-modal-title">
                <button className="role-back-button" onClick={handleCloseModal}>
                  <span>&lt;</span> Create Role
                </button>
              </div>
              {/* <button className="role-close-button" onClick={handleCloseModal}>
                <X size={20} />
              </button> */}
            </div>

            <div className="role-modal-content">
              <form onSubmit={handleSubmit}>
                <div className="role-form-row">
                  <div className="role-form-group">
                    <label htmlFor="roleName" className="role-form-label">
                      Role Name<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="roleName"
                      className={`role-form-input ${
                        errors.roleName ? "role-input-error" : ""
                      }`}
                      placeholder="Enter Role Name"
                      value={roleName}
                      onChange={(e) => {
                        setRoleName(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, roleName: "" });
                        }
                      }}
                    />
                    {errors.roleName && (
                      <div className="role-error-message">
                        {errors.roleName}
                      </div>
                    )}
                  </div>

                  <div className="role-form-group">
                    <label htmlFor="pocName" className="role-form-label">
                      POC Name<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="pocName"
                      className={`role-form-input ${
                        errors.pocName ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Name"
                      value={pocName}
                      onChange={(e) => {
                        setPocName(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocName: "" });
                        }
                      }}
                    />
                    {errors.pocName && (
                      <div className="role-error-message">{errors.pocName}</div>
                    )}
                  </div>
                </div>

                <div className="role-form-row">
                  <div className="role-form-group">
                    <label htmlFor="pocContact" className="role-form-label">
                      POC Contact Number<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="pocContact"
                      className={`role-form-input ${
                        errors.pocContact ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Contact Number"
                      value={pocContact}
                      onChange={(e) => {
                        setPocContact(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocContact: "" });
                        }
                      }}
                    />
                    {errors.pocContact && (
                      <div className="role-error-message">
                        {errors.pocContact}
                      </div>
                    )}
                  </div>

                  <div className="role-form-group">
                    <label htmlFor="pocEmail" className="role-form-label">
                      POC Mail ID<span className="role-required">*</span>
                    </label>
                    <input
                      type="email"
                      id="pocEmail"
                      className={`role-form-input ${
                        errors.pocEmail ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Mail ID"
                      value={pocEmail}
                      onChange={(e) => {
                        setPocEmail(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocEmail: "" });
                        }
                      }}
                    />
                    {errors.pocEmail && (
                      <div className="role-error-message">
                        {errors.pocEmail}
                      </div>
                    )}
                  </div>
                </div>

                <div className="role-form-group">
                  <label className="role-form-label">
                    Assign Project Architect
                    <span className="role-required">*</span>
                  </label>
                  <div
                    className={`role-architect-selection ${
                      errors.selectedArchitects ? "role-input-error" : ""
                    }`}
                  >
                    {/* Dropdown trigger */}
                    <div
                      className="role-dropdown-trigger"
                      onClick={toggleArchitectDropdown}
                    >
                      <span className="role-dropdown-placeholder">
                        {selectedArchitects.length > 0
                          ? `${selectedArchitects.length} Project Architect(s) selected`
                          : "Select Project Architect"}
                      </span>
                      <span className="role-dropdown-arrow">▼</span>
                    </div>

                    {/* Search and Dropdown */}
                    {showArchitectDropdown && (
                      <div className="role-dropdown-container">
                        <div className="role-search-container">
                          <input
                            type="text"
                            className="role-search-input"
                            placeholder="Search Project Architect"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Search className="role-search-icon" size={16} />
                        </div>

                        <div className="role-architect-list">
                          {filteredArchitects.length > 0 ? (
                            filteredArchitects.map((architect) => (
                              <div
                                key={architect.user_id}
                                className="role-architect-item"
                              >
                                <label className="role-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={selectedArchitects.some(
                                      (a) => a.user_id === architect.user_id
                                    )}
                                    onChange={() =>
                                      handleArchitectSelect(architect)
                                    }
                                  />
                                  <span>{architect.name}</span>
                                </label>
                              </div>
                            ))
                          ) : (
                            <div className="role-no-results">
                              No architects found
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Selected Architects */}
                    {selectedArchitects.length > 0 && (
                      <div className="role-selected-architects">
                        {selectedArchitects.map((architect) => (
                          <div
                            key={architect.user_id}
                            className="role-architect-tag"
                          >
                            <span>{architect.name}</span>
                            <button
                              type="button"
                              className="role-tag-remove"
                              onClick={() => removeArchitect(architect.user_id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.selectedArchitects && (
                    <div className="role-error-message">
                      {errors.selectedArchitects}
                    </div>
                  )}
                </div>

                {/* <div className="role-form-group">
                  <label
                    htmlFor="calculationMethodEdit"
                    className="role-form-label"
                  >
                    Select Calculation Method
                    <span className="role-required">*</span>
                  </label>
                  <Select
                    inputId="calculationMethodEdit"
                    classNamePrefix="role-form-select"
                    styles={customStyles}
                    options={calculationOptions}
                    value={calculationOptions.find(
                      (opt) => opt.value === calculationMethod
                    )}
                    onChange={(selectedOption) => {
                      const value = selectedOption?.value || "";
                      setCalculationMethod(value);
                      if (value) {
                        setErrors({ ...errors, calculationMethod: "" });
                      }
                    }}
                    placeholder="Select Calculation Method"
                  />
                  {errors.calculationMethod && (
                    <div className="role-error-message">
                      {errors.calculationMethod}
                    </div>
                  )}
                </div> */}

                <div className="role-form-actions">
                  {/* <button type="button" className="role-add-new-button">
                                        + Add New
                                    </button> */}
                  <div className="role-form-buttons">
                    <button
                      type="button"
                      className="role-cancel-button"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="role-save-button"
                      disabled={formLoading}
                    >
                      {formLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {isEditModalOpen && (
        <div className="role-modal-overlay">
          <div className="role-modal">
            <div className="role-modal-header">
              <div className="role-modal-title">
                <button className="role-back-button" onClick={handleCloseModal}>
                  <span>
                    <ChevronLeft style={{ marginTop: "3px" }} />
                  </span>{" "}
                  Edit Role
                </button>
              </div>
              {/* <button className="role-close-button" onClick={handleCloseModal}>
                <X size={20} />
              </button> */}
            </div>

            <div className="role-modal-content">
              <form onSubmit={handleUpdate}>
                <div className="role-form-row">
                  <div className="role-form-group">
                    <label htmlFor="roleNameEdit" className="role-form-label">
                      Role Name<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="roleNameEdit"
                      className={`role-form-input ${
                        errors.roleName ? "role-input-error" : ""
                      }`}
                      placeholder="Enter Role Name"
                      value={roleName}
                      onChange={(e) => {
                        setRoleName(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, roleName: "" });
                        }
                      }}
                      required
                    />
                    {errors.roleName && (
                      <div className="role-error-message">
                        {errors.roleName}
                      </div>
                    )}
                  </div>

                  <div className="role-form-group">
                    <label htmlFor="pocNameEdit" className="role-form-label">
                      POC Name<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="pocNameEdit"
                      className={`role-form-input ${
                        errors.pocName ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Name"
                      value={pocName}
                      onChange={(e) => {
                        setPocName(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocName: "" });
                        }
                      }}
                      required
                    />
                    {errors.pocName && (
                      <div className="role-error-message">{errors.pocName}</div>
                    )}
                  </div>
                </div>

                <div className="role-form-row">
                  <div className="role-form-group">
                    <label htmlFor="pocContactEdit" className="role-form-label">
                      POC Contact Number<span className="role-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="pocContactEdit"
                      className={`role-form-input ${
                        errors.pocContact ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Contact Number"
                      value={pocContact}
                      onChange={(e) => {
                        setPocContact(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocContact: "" });
                        }
                      }}
                      required
                    />
                    {errors.pocContact && (
                      <div className="role-error-message">
                        {errors.pocContact}
                      </div>
                    )}
                  </div>

                  <div className="role-form-group">
                    <label htmlFor="pocEmailEdit" className="role-form-label">
                      POC Mail ID<span className="role-required">*</span>
                    </label>
                    <input
                      type="email"
                      id="pocEmailEdit"
                      className={`role-form-input ${
                        errors.pocEmail ? "role-input-error" : ""
                      }`}
                      placeholder="Enter POC Mail ID"
                      value={pocEmail}
                      onChange={(e) => {
                        setPocEmail(e.target.value);
                        if (e.target.value.trim()) {
                          setErrors({ ...errors, pocEmail: "" });
                        }
                      }}
                      required
                    />
                    {errors.pocEmail && (
                      <div className="role-error-message">
                        {errors.pocEmail}
                      </div>
                    )}
                  </div>
                </div>

                <div className="role-form-group">
                  <label className="role-form-label">
                    Assign Project Architect
                    <span className="role-required">*</span>
                  </label>
                  <div
                    className={`role-architect-selection ${
                      errors.selectedArchitects ? "role-input-error" : ""
                    }`}
                  >
                    {/* Dropdown trigger */}
                    <div
                      className="role-dropdown-trigger"
                      onClick={toggleArchitectDropdown}
                    >
                      <span className="role-dropdown-placeholder">
                        {selectedArchitects.length > 0
                          ? `${selectedArchitects.length} Project Architect(s) selected`
                          : "Select Project Architect"}
                      </span>
                      <span className="role-dropdown-arrow">▼</span>
                    </div>

                    {/* Search and Dropdown */}
                    {showArchitectDropdown && (
                      <div className="role-dropdown-container">
                        <div className="role-search-container">
                          <input
                            type="text"
                            className="role-search-input"
                            placeholder="Search Project Architect"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <Search className="role-search-icon" size={16} />
                        </div>

                        <div className="role-architect-list">
                          {filteredArchitects.length > 0 ? (
                            filteredArchitects.map((architect) => (
                              <div
                                key={architect.user_id}
                                className="role-architect-item"
                              >
                                <label className="role-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={selectedArchitects.some(
                                      (a) => a.user_id === architect.user_id
                                    )}
                                    onChange={() =>
                                      handleArchitectSelect(architect)
                                    }
                                  />
                                  <span>{architect.name}</span>
                                </label>
                              </div>
                            ))
                          ) : (
                            <div className="role-no-results">
                              No architects found
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Selected Architects */}
                    {selectedArchitects.length > 0 && (
                      <div className="role-selected-architects">
                        {selectedArchitects.map((architect) => (
                          <div
                            key={architect.user_id}
                            className="role-architect-tag"
                          >
                            <span>{architect.name}</span>
                            <button
                              type="button"
                              className="role-tag-remove"
                              onClick={() => removeArchitect(architect.user_id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.selectedArchitects && (
                    <div className="role-error-message">
                      {errors.selectedArchitects}
                    </div>
                  )}
                </div>

                {/* <div className="role-form-group">
                  <label
                    htmlFor="calculationMethodEdit"
                    className="role-form-label"
                  >
                    Select Calculation Method
                    <span className="role-required">*</span>
                  </label>

                  <Select
                    inputId="calculationMethodEdit"
                    classNamePrefix="role-form-select"
                    styles={customStyles}
                    options={calculationOptions}
                    value={calculationOptions.find(
                      (opt) => opt.value === calculationMethod
                    )}
                    onChange={(selected) => {
                      const value = selected?.value || "";
                      setCalculationMethod(value);
                      if (value) {
                        setErrors({ ...errors, calculationMethod: "" });
                      }
                    }}
                    placeholder="Select Calculation Method"
                  />

                  {errors.calculationMethod && (
                    <div className="role-error-message">
                      {errors.calculationMethod}
                    </div>
                  )}
                </div> */}

                <div className="role-form-actions">
                  <button
                    type="button"
                    className="role-delete-button"
                    onClick={confirmDeleteRole}
                  >
                    <Trash2 size={16} />
                    Delete Role
                  </button>
                  <div className="role-form-buttons">
                    <button
                      type="button"
                      className="role-cancel-button"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="role-save-button"
                      disabled={formLoading}
                    >
                      {formLoading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showWarningModal && (
        <div className="warning-modal-overlay">
          <div className="warning-modal-container">
            <button
              className="warning-modal-close"
              onClick={() => setShowWarningModal(false)}
            >
              ×
            </button>
            <h3 className="warning-modal-text">
              <CircleAlert size={20} />
              Role cannot be deleted until all associated Projects are deleted.
            </h3>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="role-delete-modal-overlay">
          <div className="role-delete-modal-container">
            <div className="role-delete-modal-content">
              <h3>Confirm Delete Role - {currentRole.role_name}?</h3>
              <div className="role-delete-modal-buttons">
                <button
                  className="role-delete-modal-button role-delete-cancel"
                  onClick={cancelDelete}
                >
                  No
                </button>
                <button
                  className="role-delete-modal-button role-delete-confirm"
                  onClick={confirmDeleteUser}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActiveRoles;
